import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { FormControl, GridLegacy, Typography } from '@mui/material';
import { useSelector } from 'store';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { FieldValues, useForm } from 'react-hook-form';
import { HookFormToggle } from 'ui-component/HookFormComponents';
import { getCopy } from 'views/org/Preferences/copy';
import { useGetOrgQuery, useUpdateOrgMutation } from 'store/slices/apiV1/org';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { useGetPriceModelQuery } from 'store/slices/apiV1/purchasing';
import { useEffect, useState } from 'react';
import { handleErr } from 'utils/functions';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { gridSpacing } from 'constants/themeConstants';
import useSnackbar from 'hooks/useSnackbar';

const InventoryPreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const { data: priceModel } = useGetPriceModelQuery(
    org?.priceModel.id || skipToken
  );
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();

  const copy = getCopy(priceModel);

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FieldValues>();
  const [isEditing, setIsEditing] = useState(false);
  const watchAllValues = watch();

  useEffect(() => {
    if (org) {
      reset({
        mslResetOnReceipt: org.mslResetOnReceipt,
        lcrInspectionOnReceipt: org.lcrInspectionOnReceipt,
        dimensionalInspectionOnReceipt: org.dimensionalInspectionOnReceipt,
        inspectionPhotoOnReceipt: org.inspectionPhotoOnReceipt,
        packagePhotoOnReceipt: org.packagePhotoOnReceipt,
      });
    }
  }, [org]);

  const onSubmit = async (payload: any) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload,
        }).unwrap();
        dispatchSuccessSnackbar('Preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardSectionWithAction
        contents={
          <FormControl fullWidth>
            <GridLegacy container spacing={gridSpacing}>
              {copy.toggles.map((formField) => (
                <GridLegacy item xs={12} key={formField.name}>
                  <HookFormToggle
                    name={formField.name}
                    control={control}
                    label={formField.label}
                    disabled={!isEditing}
                  />
                  <Typography>
                    {watchAllValues[formField.name]
                      ? formField.description.true
                      : formField.description.false}
                  </Typography>
                </GridLegacy>
              ))}
            </GridLegacy>
          </FormControl>
        }
        action={
          isEditing ? (
            <SegmentLoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              loading={isUpdatingOrg}
              permissionScope={{
                app: ALL_APPS.MANAGED_INVENTORY.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              {isDirty ? 'Save' : 'Cancel'}
            </SegmentLoadingButton>
          ) : (
            <SegmentButton
              type="button"
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ float: 'right' }}
              permissionScope={{
                app: ALL_APPS.MANAGED_INVENTORY.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              Edit
            </SegmentButton>
          )
        }
        title="Receiving Preferences"
      />
    </form>
  );
};

export default InventoryPreferences;
